import { useCallback, useState } from "react";
import type { FieldValues, Path, UseFormClearErrors, UseFormRegister, UseFormSetError } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import type { AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { fetchAdmissionPrefillPayloadByApplicantId } from "@/src/utils/tasks/fetchAdmissionPrefillPayloadByApplicantId";

interface AOPStudentIdInputProps<TForm extends FieldValues> {
  register: UseFormRegister<TForm>;
  setError: UseFormSetError<TForm>;
  clearErrors: UseFormClearErrors<TForm>;
  applyTaskPrefill: (payload: AdmissionTaskPrefillPayload) => void;
  lastPrefilledStudentIdRef: React.MutableRefObject<string>;
  onCopy?: () => void;
  onClear?: () => void;
  disabled?: boolean;
  studentIdField?: Path<TForm>;
}

const AOPStudentIdInput = <TForm extends FieldValues>({
  register,
  setError,
  clearErrors,
  applyTaskPrefill,
  lastPrefilledStudentIdRef,
  onCopy,
  onClear,
  disabled = false,
  studentIdField = "student_id" as Path<TForm>,
}: AOPStudentIdInputProps<TForm>) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const studentIdRegister = register(studentIdField);
  const isInputDisabled = disabled || isLoading;

  const loadStudentById = useCallback(
    async (rawStudentId: string) => {
      const normalizedId = rawStudentId.trim();
      if (!normalizedId || isLoading || disabled) return;
      if (normalizedId === lastPrefilledStudentIdRef.current) return;

      setIsLoading(true);
      clearErrors(studentIdField);
      dispatch(setIsPreloyder(true));

      try {
        const payload = await fetchAdmissionPrefillPayloadByApplicantId(normalizedId);
        if (!payload) {
          setError(studentIdField, {
            type: "manual",
            message: "User not found",
          });
          notify({
            title: "User not found",
            message: `No user found with ID "${normalizedId}".`,
            type: "error",
          });
          return;
        }

        applyTaskPrefill(payload);
        lastPrefilledStudentIdRef.current = normalizedId;
        notify({
          title: "User loaded",
          message: "Form fields were prefilled from the user.",
          type: "success",
        });
      } catch (error) {
        console.error("[AdmissionOffice] Student ID lookup failed:", error);
        setError(studentIdField, {
          type: "manual",
          message: "Failed to load user",
        });
        notify({
          title: "User lookup failed",
          message: "Could not load user data. Please try again.",
          type: "error",
        });
      } finally {
        setIsLoading(false);
        dispatch(setIsPreloyder(false));
      }
    },
    [
      applyTaskPrefill,
      clearErrors,
      disabled,
      dispatch,
      isLoading,
      lastPrefilledStudentIdRef,
      notify,
      setError,
      studentIdField,
    ],
  );

  return (
    <div className="form-group-fields-input">
      <div className="form-group-fields-input-label">
        <label htmlFor="student_id">Student Id</label>
        <div className="form-group-fields-input-label-line"></div>
      </div>
      <input
        className="student_id"
        id="student_id"
        type="text"
        disabled={isInputDisabled}
        {...studentIdRegister}
        onBlur={(event) => {
          studentIdRegister.onBlur(event);
          void loadStudentById(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          void loadStudentById((event.target as HTMLInputElement).value);
        }}
      />
      <div className="form-group-fields-input-svg">
        {onClear ? (
          <div
            className="form-group-fields-input-svg-delete"
            onClick={() => {
              if (isInputDisabled) return;
              onClear();
            }}
          >
            <AODSvgExport.TrashSvg />
          </div>
        ) : null}
        {onClear && onCopy ? <div className="form-group-fields-input-svg-line"></div> : null}
        {onCopy ? (
          <div
            className="form-group-fields-input-svg-copy"
            onClick={() => {
              if (isInputDisabled) return;
              onCopy();
            }}
          >
            <AODSvgExport.FullNameCopySvg />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AOPStudentIdInput;
